import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {NgxSpinnerService} from 'ngx-spinner';

import {EMPTY, firstValueFrom, Observable, of} from 'rxjs';
import {filter, first, map, startWith, switchMap, takeUntil} from 'rxjs/operators';

import {FormManagerService, keys} from '@sim/form';
import {MediaService, UIMediaService} from '@sim/media';

import {BaseGuardFormComponent} from '@atmi/core/components/base-guard-form.component';
import {filePath} from '@atmi/core/injection';
import {Attachment, cardS} from '@atmi/core/models';
import {Status} from '@atmi/core/models/status';
import {CardsApiService} from '@atmi/core/services/cards-api.service';
import {checkUniqueCodeDuplicate} from '@atmi/core/validators';

import {Collection} from '@src/app/features/collections/models/collection';
import {CollectionApiService} from '@src/app/features/collections/services/collection-api.service';
import {CollectionItemApiService} from '@src/app/features/collections/services/collection-item-api.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-card-s-manager',
  styleUrls: ['./card-s-manager.component.scss'],
  templateUrl: './card-s-manager.component.html',
})
export class CardSManagerComponent extends BaseGuardFormComponent<cardS.CardS> {

  get imageUrl() {
    const originalUrl = this.formEditData
      ? this.formEditData.previewUrl || ''
      : '';

    return this.formEditData && originalUrl ? this.fileUrl + originalUrl : undefined;
  }

  readonly activatedTabs: Record<number, boolean> = {
    0: true,
    1: true,
    2: false,
    3: false,
    4: false,
    5: true,
    6: true,
    7: false,
    8: false,
  };

  readonly isOperaMadre$: Observable<boolean>;
  readonly isChildCard$: Observable<boolean>;

  collections: Array<Collection & { id: string }> = [];
  collectionsLoading = false;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly mediaApi: MediaService,
    private readonly uiMediaService: UIMediaService,
    protected readonly router: Router,
    protected readonly translate: TranslateService,
    protected readonly spinner: NgxSpinnerService,
    protected readonly snackBarService: MatSnackBar,
    protected readonly route: ActivatedRoute,
    protected readonly formManager: FormManagerService,
    protected readonly cardsApi: CardsApiService,
    protected readonly cd: ChangeDetectorRef,
    private readonly collectionApiService: CollectionApiService,
    private readonly collectionItemApi: CollectionItemApiService,
  ) {
    super(
      snackBarService,
      translate,
      route,
      formManager,
      spinner,
      router,
      cd,
      cardS.CardSMeta,
      ((id) => id ? cardsApi.getS(id as string) : of(undefined)),
      '/cards',
      {card: 1},
    );

    this.activatedTabs[this.activeTabIndex] = true;

    this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      takeUntil(this.onDestroy$),
    ).subscribe((form) => {
      const limit = this.mode === 'edit' ? 1 : 0;
      form.controls.codiceUnico.setAsyncValidators([checkUniqueCodeDuplicate(this.cardsApi.indexS.bind(this.cardsApi), limit)]);
      form.controls.codiceUnico.updateValueAndValidity();
      
      if (this.entityId) {
        this.getCollections();
      }
    });

    this.isOperaMadre$ = this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      switchMap((form) => this.entityForm.valueChanges.pipe(startWith(form.value))),
      map((card: cardS.CardS) => {
        const isOperaMadreEnable = !!(keys(card.operaMadre).some((key) =>
          !!this.entityForm.controls.operaMadre.value[key
            ]));

        return isOperaMadreEnable;
      }),
    );

    this.isChildCard$ = this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      switchMap((form) => this.entityForm.valueChanges.pipe(startWith(form.value))),
      map((card: cardS.CardS) => !!card.benePadre),
    );
  }

  addConcrete(card: cardS.CardS) {
    return this.cardsApi.postS(card, 's');
  }

  removeConcrete(card: cardS.CardS) {
    if (card.id) {
      return this.cardsApi.removeS(card.id);
    }
    return EMPTY;
  }

  editConcrete(card: cardS.CardS) {
    return this.cardsApi.putS(card, 's');
  }

  resetFormConcrete() {
    return this.entityForm.reset();
  }

  changeStatusHandler(status: Status) {
    if (!this.entityControls) {
      throw new Error('Cannot set status without a valid controls');
    }
    if (!this.entityControls.status) {
      throw new Error('Cannot set status without a valid control');
    }
    this.entityControls.status.setValue(status);
    this.entityControls.status.updateValueAndValidity();
  }

  async showMediaHandler() {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return;
    }
    const attachmentToShow = attachments[0];
    const media = await firstValueFrom(this.mediaApi.get(attachmentToShow.file).pipe(first()));
    this.uiMediaService.dispatchViewMediaAction(media);
  }

  get originalImageDownloadUrl(): string | undefined {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return undefined;
    }
    return this.mediaApi.generateBinaryUrl(attachments[0].file);
  }

  async downloadOriginalImage() {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return;
    }
    const fileId = attachments[0].file;
    const blob = await firstValueFrom(this.mediaApi.getBinary(fileId).pipe(first()));
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = this.entityForm?.value?.codiceUnico || 'download';
    const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/png' ? 'png' : blob.type === 'application/pdf' ? 'pdf' : '';
    a.download = ext ? `${baseName}.${ext}` : baseName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  reloadPage() {
    window.location.reload();
  }

  private async getCollections() {
    this.collectionsLoading = true;
    this.collections = [];
    
    try {
      const res = await firstValueFrom(this.collectionApiService.index().pipe(first()));
      const collections = res.data.filter(c => Boolean(c.id));

      for (const collection of collections) {
        const collectionItems = await firstValueFrom(this.collectionItemApi.index(collection?.id || '').pipe(first()));
        if (collectionItems.data.findIndex((item) => item.objectId === this.entityId) !== -1) {
          this.collections.push(collection as Collection & { id: string });
        }
      }
      
      this.cd.markForCheck();
    } catch (e) {
      console.error(e);
    } finally {
      this.collectionsLoading = false;
      this.cd.markForCheck();
    }
  }

  navigateToCollection(collectionId: string) {
    this.router.navigate(['/collections/manager', collectionId]);
  }

}
